import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IoCheckmark } from 'react-icons/io5'
import { useAuth } from '../../contexts/AuthContext'
import { miniTutorialService } from '../../services/miniTutorialService'
import '../ui/animations.css'

interface MiniTipsProps {
  tutorialActive?: boolean
}

type MiniTipMode = 'center' | 'tooltip'

interface MiniTipBinding {
  key: string
  selector?: string
  resolver?: (root: Document) => Element[]
  defer?: boolean
  afterOpenResolver?: (root: Document) => Element | null
  text: string
  mode?: MiniTipMode
  // opcional: condiciona render/indicador por rota/estado
  guard?: () => boolean
  // opcional: onde anexar a bolinha dentro do elemento raiz
  indicatorSelector?: string
}

interface ActiveMiniTip {
  key: string
  text: string
  anchorRect?: DOMRect | null
  mode: MiniTipMode
}

// Utilitário para localizar elementos por texto, com normalização sem acentos
const TEXT_TAGS = ['h1','h2','h3','h4','h5','p','div','span','section','header','main']
const norm = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
const findByText = (root: Document, substrings: string[]): HTMLElement | null => {
  const wanted = substrings.map(norm)
  const nodes = Array.from(root.querySelectorAll(TEXT_TAGS.join(','))) as HTMLElement[]
  for (const el of nodes) {
    const t = norm(el.textContent || '')
    if (wanted.every(w => t.includes(w))) return el
  }
  return null
}

// Indicadores visuais (bolinha vermelha) para itens ainda não vistos
function ensureIndicator(el: Element, key: string) {
  try {
    const host = el as HTMLElement
    if (!host) return
    if (host.querySelector(`span[data-minitip2-dot-key="${key}"]`)) return
    const dot = document.createElement('span')
    dot.setAttribute('data-minitip2-dot-key', key)
    dot.style.position = 'absolute'
    dot.style.top = '-4px'
    dot.style.right = '-4px'
    dot.style.width = '10px'
    dot.style.height = '10px'
    dot.style.borderRadius = '9999px'
    dot.style.background = '#ef4444'
    dot.style.boxShadow = '0 0 0 2px white'
    dot.style.pointerEvents = 'none'
    ;(dot.style as any).zIndex = 'auto'
    const currentPos = window.getComputedStyle(host).position
    if (!currentPos || currentPos === 'static') {
      host.style.position = 'relative'
    }
    host.appendChild(dot)
  } catch { void 0; }
}

function removeIndicatorsForKey(key: string) {
  try {
    document.querySelectorAll(`span[data-minitip2-dot-key="${key}"]`).forEach((n) => n.parentElement?.removeChild(n))
  } catch { void 0; }
}

function chunkTextByWords(text: string, wordsPerLine = 4): string[] {
  const sanitized = (text || '').replace(/\s+/g, ' ').trim()
  if (!sanitized) return []
  const words = sanitized.split(' ')
  const lines: string[] = []
  for (let i = 0; i < words.length; i += wordsPerLine) {
    lines.push(words.slice(i, i + wordsPerLine).join(' '))
  }
  return lines
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 10001,
}

const CENTER_CONTAINER: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: 0,
  right: 0,
  transform: 'translateY(-50%)',
  margin: '0 auto',
  width: 'calc(100% - 32px)',
  maxWidth: 260,
  zIndex: 10002,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  textAlign: 'center',
  animation: 'fadeInScale 0.3s ease-out forwards',
  opacity: 0,
}

const BUBBLE_CENTER: React.CSSProperties = {
  width: '100%',
  maxWidth: 260,
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '6px 12px',
  borderRadius: 20,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  fontSize: 13.5,
  lineHeight: 1.42,
  border: '2px solid #8B5CF6',
  position: 'relative',
  textAlign: 'center',
  animation: 'slideInFromBottom 0.3s ease-out 0.1s forwards',
  opacity: 0,
}

const BUBBLE_TAIL: React.CSSProperties = {
  content: '""',
  position: 'absolute',
  top: -12,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '8px solid transparent',
  borderRight: '8px solid transparent',
  borderBottom: '12px solid #8B5CF6',
}

const IMG_APIS: React.CSSProperties = {
  width: 240,
  height: 'auto',
  pointerEvents: 'none',
  margin: '0 auto',
  animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatVertical 4s ease-in-out 0.4s infinite',
}

const BTN: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
  animation: 'slideInFromBottom 0.3s ease-out 0.2s forwards',
  opacity: 0,
  alignSelf: 'center',
}

const BUBBLE_TEXT: React.CSSProperties = {
  margin: 0,
  fontWeight: 500,
  fontSize: 13.5,
  lineHeight: 1.42,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
}

const BUBBLE_TEXT_WRAPPER: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  alignItems: 'center',
}

const TOOLTIP_CONTAINER_BASE: React.CSSProperties = {
  position: 'fixed',
  zIndex: 10002,
  pointerEvents: 'none',
}

const TOOLTIP_BOX: React.CSSProperties = {
  maxWidth: 220,
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '6px 12px',
  borderRadius: 14,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  fontSize: 13,
  lineHeight: 1.42,
  border: '2px solid #8B5CF6',
  animation: 'fadeInScale 0.3s ease-out forwards',
  opacity: 0,
}

const TOOLTIP_BTN: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 12,
  boxShadow: '0 2px 6px rgba(139, 92, 246, 0.3)',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  transition: 'all 0.2s ease-out',
}

const BINDINGS: MiniTipBinding[] = [
  // Dashboard principal
  {
    key: 'dashboardSummary',
    selector: '[data-tutorial-anchor="dashboard-summary"]',
    text: 'Resumo de hoje: pontos, nível e atalhos do dia. Toque e explore para navegar rapidamente.',
    mode: 'tooltip',
    guard: () => {
      try { const p = window.location.pathname || ''; return p === '/' || p === '/dashboard' } catch { return true }
    }
  },
  // Resumo de Hoje - card inteiro
  {
    key: 'home-today-summary',
    resolver: (root) => {
      const h3s = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      const h = h3s.find(x => norm(x.textContent || '').includes('resumo de hoje'))
      return h ? [h] : []
    },
    text: 'Resumo de Hoje: visão rápida das atividades do dia (doses, lembretes, pontos, positivos, streaks e quiz).',
    mode: 'center',
    indicatorSelector: '^closest:div.rounded-card',
    guard: () => {
      try { const p = window.location.pathname || ''; return p === '/' || p === '/dashboard' } catch { return true }
    }
  },
  // Footer
  {
    key: 'footer-remedies',
    resolver: (root) => {
      const links = Array.from(root.querySelectorAll('a')) as HTMLAnchorElement[]
      return links.filter(a => norm((a.textContent || '').trim()) === 'remedios')
    },
    text: 'Biblioteca de Remédios: consulte informações sobre homeopatias e potências.',
    mode: 'center',
    indicatorSelector: 'span.flex',
    defer: true,
    afterOpenResolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h1')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').trim().toLowerCase().includes('biblioteca de remédios')) || null
    }
  },
  {
    key: 'footer-profile',
    resolver: (root) => {
      const links = Array.from(root.querySelectorAll('a')) as HTMLAnchorElement[]
      return links.filter(a => norm((a.textContent || '').trim()) === 'perfil')
    },
    text: 'Página de Perfil: gerencie seus dados pessoais e da família.',
    mode: 'center',
    defer: true,
    indicatorSelector: 'span.flex',
    afterOpenResolver: (root) => {
      const h1s = Array.from(root.querySelectorAll('h1')) as HTMLElement[]
      const found = h1s.find(h => norm(h.textContent || '') === 'meu perfil')
      return found || null
    }
  },
  {
    key: 'footer-sos',
    resolver: (root) => {
      const btns = Array.from(root.querySelectorAll('button')) as HTMLButtonElement[]
      const candidates = btns.filter((b) => !!b.querySelector('i.ri-heart-pulse-line'))
      const centered = candidates.find(b => ((b.parentElement?.className || '').includes('left-1/2')))
      return centered ? [centered] : (candidates.length > 0 ? [candidates[0]] : [])
    },
    text: 'SOS: atalho de emergência com orientações rápidas.',
    mode: 'center',
    defer: true,
    afterOpenResolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').trim().toLowerCase().includes('sos - médico homeopata virtual')) || null
    }
  },
  // Página do membro
  {
    key: 'member-quiz',
    selector: '[data-quiz-trigger]',
    resolver: (root) => Array.from(root.querySelectorAll('[data-quiz-trigger]')) as HTMLElement[],
    text: 'Quiz diário: responda perguntas rápidas para ganhar pontos e acompanhar sua jornada.',
    mode: 'center',
    defer: true,
    indicatorSelector: '^closest:div.relative.flex',
    guard: () => {
      try { return (window.location.pathname || '').startsWith('/member/') } catch { return false }
    },
    afterOpenResolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h2')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').trim().toLowerCase().includes('quiz de homeopatia')) || null
    }
  },
  // Adesão (card de estatísticas rápidas)
  {
    key: 'member-adherence',
    resolver: (root) => {
      const ps = Array.from(root.querySelectorAll('p')) as HTMLElement[]
      const matches = ps.filter(p => norm(p.textContent || '') === 'adesao')
      const cards = matches.map(p => (p.closest('div.rounded-2xl') as Element) || p)
      return cards as Element[]
    },
    text: 'Adesão: porcentagem de cumprimento do seu tratamento. Quanto maior, melhor!',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  // Dias sem interrupção (card de estatísticas rápidas)
  {
    key: 'member-streak-treatment',
    resolver: (root) => {
      const ps = Array.from(root.querySelectorAll('p')) as HTMLElement[]
      const matches = ps.filter(p => norm(p.textContent || '').includes('dias sem interrupcao'))
      const cards = matches.map(p => (p.closest('div.rounded-2xl') as Element) || p)
      return cards as Element[]
    },
    text: 'Dias sem interrupção: sua sequência atual mantendo o tratamento em dia.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  // Dias sem doença (counters do cabeçalho)
  {
    key: 'member-streak-illness',
    resolver: (root) => {
      const icons = Array.from(root.querySelectorAll('i.ri-heart-pulse-line')) as HTMLElement[]
      const containers = icons
        .map(i => i.closest('div.text-center'))
        .filter((x): x is Element => !!x)
      return containers
    },
    text: 'Dias sem doenças: sequência de dias sem eventos de saúde registrados.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  // Dias sem Alopático (counters do cabeçalho)
  {
    key: 'member-streak-allopathic',
    resolver: (root) => {
      const icons = Array.from(root.querySelectorAll('i.ri-capsule-line')) as HTMLElement[]
      const containers = icons
        .map(i => i.closest('div.text-center'))
        .filter((x): x is Element => !!x)
      return containers
    },
    text: 'Dias sem alopáticos: sequência de dias sem uso de medicamentos alopáticos.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  // Log de Lembretes (seção)
  {
    key: 'member-reminders-log',
    resolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      const h = headers.find(h3 => norm(h3.textContent || '').includes('log de lembretes'))
      const card = h ? (h.closest('div.rounded-3xl') as Element | null) : null
      return card ? [card] : []
    },
    text: 'Log de Lembretes: visualize e edite seus lembretes recentes.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  {
    key: 'member-points-history',
    resolver: (root) => Array.from(root.querySelectorAll('button')).filter((b) => !!b.querySelector('i.ri-history-line')),
    text: 'Histórico de pontos: veja quando e como você ganhou (ou perdeu) pontos.',
    mode: 'center',
    defer: true,
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } },
    afterOpenResolver: (root) => {
      // Texto exato fornecido: "Aqui você acompanha os pontos para cada ação que você faz no aplicativo"
      const byText = findByText(root, ['aqui voce acompanha os pontos para cada acao que voce faz no aplicativo'])
      if (byText) return byText
      const headers = Array.from(root.querySelectorAll('h2')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').trim().toLowerCase().includes('histórico de pontos')) || null
    }
  },
  {
    key: 'member-badges-button',
    resolver: (root) => Array.from(root.querySelectorAll('button')).filter((b) => !!b.querySelector('i.ri-medal-line')),
    text: 'Meus Badges: veja suas conquistas e metas atingidas.',
    mode: 'center',
    defer: true,
    afterOpenResolver: (root) => {
      // Texto exato fornecido: "Aqui você acompanha suas vitórias conforme vai progredindo na história homeopática"
      const byText = findByText(root, ['aqui voce acompanha suas vitorias conforme vai progredindo na historia homeopatica'])
      if (byText) return byText
      const headers = Array.from(root.querySelectorAll('h2')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').toLowerCase().includes('galeria de badges')) || null
    }
  },
  {
    key: 'member-badges-card',
    resolver: (root) => {
      const labels = Array.from(root.querySelectorAll('p')) as HTMLElement[]
      const p = labels.find(x => norm(x.textContent || '') === 'conquistas')
      const card = p ? (p.closest('div.rounded-2xl') as Element | null) : null
      return card ? [card] : []
    },
    text: 'Conquistas: total de badges desbloqueadas para este membro.',
    mode: 'center',
    defer: true,
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } },
    afterOpenResolver: (root) => {
      const byText = findByText(root, ['aqui voce acompanha suas vitorias conforme vai progredindo na historia homeopatica'])
      if (byText) return byText
      const headers = Array.from(root.querySelectorAll('h2')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').toLowerCase().includes('galeria de badges')) || null
    }
  },
  {
    key: 'member-today-homeopathies',
    resolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      const h = headers.find(h3 => norm(h3.textContent || '') === 'homeopatias de hoje')
      const card = h ? (h.closest('div.rounded-3xl') as Element | null) : null
      return card ? [card] : []
    },
    text: 'Homeopatias de Hoje: suas doses e horários do dia atual.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  {
    key: 'member-treatments-history',
    selector: '#treatment-history',
    text: 'Histórico de Tratamentos: visão consolidada dos seus tratamentos com progresso.',
    mode: 'center',
  },
  {
    key: 'member-medical-record',
    resolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      const h = headers.find(h3 => norm(h3.textContent || '') === 'registro medico')
      const card = h ? (h.closest('div.rounded-3xl') as Element | null) : null
      return card ? [card] : []
    },
    text: 'Registro Médico: anote doenças ou uso de alopáticos para acompanhar seu progresso.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } }
  },
  {
    key: 'member-medical-record-cta',
    resolver: (root) => Array.from(root.querySelectorAll('button')).filter((b) => (b.textContent || '').trim().toLowerCase().includes('fazer registro médico')),
    text: 'Aqui você registra doenças e medicamentos alopáticos. Preencha e salve para manter seu histórico.',
    mode: 'center',
    defer: true,
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } },
    afterOpenResolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').trim().toLowerCase() === 'registro médico') || null
    }
  },
  {
    key: 'member-health-diary',
    resolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      const h = headers.find(h3 => norm(h3.textContent || '') === 'diario de saude')
      const card = h ? (h.closest('div.rounded-3xl') as Element | null) : null
      return card ? [card] : []
    },
    text: 'Diário de Saúde: registre como você se sente e eventos importantes.',
    mode: 'center',
    guard: () => { try { return (window.location.pathname || '').startsWith('/member/') } catch { return false } },
  },
  {
    key: 'member-health-diary-cta',
    resolver: (root) => Array.from(root.querySelectorAll('button')).filter((b) => (b.textContent || '').trim().toLowerCase().includes('ver histórico completo')),
    text: 'Este é o histórico completo de saúde. Use filtros para analisar sua evolução.',
    mode: 'center',
    defer: true,
    afterOpenResolver: (root) => {
      const headers = Array.from(root.querySelectorAll('h3')) as HTMLElement[]
      return headers.find(h => (h.textContent || '').trim().toLowerCase().includes('histórico de saúde')) || null
    }
  },
]

function hasSeen(key: string): boolean {
  try { return localStorage.getItem(`minitip:${key}:seen`) === '1' } catch { return true }
}

// Marca como visto tanto no localStorage quanto no banco
async function setSeen(key: string, userId: string | undefined) {
  try {
    // 1. Atualizar localStorage imediatamente (UX rápida)
    localStorage.setItem(`minitip:${key}:seen`, '1')
    
    // 2. Persistir no banco (em background)
    if (userId) {
      await miniTutorialService.markAsSeen(userId, key)
    }
    
    // 3. Notificar HelpCenterPage para atualizar os botões
    window.dispatchEvent(new CustomEvent('magistral:minitip:state-changed', { 
      detail: { key, seen: true, enabled: false } 
    }))
  } catch (err) {
    console.error('[MiniTips2] Error in setSeen:', err)
  }
}

// Espera até que afterOpenResolver encontre um elemento ou timeout
async function waitForAfterOpen(binding: MiniTipBinding, timeoutMs = 2000, intervalMs = 80): Promise<HTMLElement | null> {
  if (!binding.afterOpenResolver) return null
  const start = Date.now()
  return new Promise((resolve) => {
    const tick = () => {
      try {
        const el = binding.afterOpenResolver!(document) as HTMLElement | null
        if (el) return resolve(el)
      } catch { void 0; }
      if (Date.now() - start >= timeoutMs) return resolve(null)
      window.setTimeout(tick, intervalMs)
    }
    tick()
  })
}

const MiniTips2: React.FC<MiniTipsProps> = ({ tutorialActive = false }) => {
  const { user } = useAuth()
  const [active, setActive] = useState<ActiveMiniTip | null>(null)
  const moRef = useRef<MutationObserver | null>(null)
  const bindings = useMemo(() => BINDINGS, [])
  
  // Sincronizar estado do banco com localStorage ao montar
  useEffect(() => {
    if (user?.id) {
      miniTutorialService.syncWithLocalStorage(user.id).catch((err) => {
        console.error('[MiniTips2] Error syncing with database:', err)
      })
    }
  }, [user?.id])

  useEffect(() => {
    if (tutorialActive) return
    const handlers: Array<{ key: string; el: Element; onClick: (e: Event) => void }> = []

    const bindAll = () => {
      bindings.forEach((b) => {
        if (hasSeen(b.key)) return
        if (b.guard && !b.guard()) return
        const selected = b.selector ? Array.from(document.querySelectorAll(b.selector)) : []
        const resolved = b.resolver ? b.resolver(document) : []
        const els = Array.from(new Set([...(selected as Element[]), ...(resolved as Element[])]))
        els.forEach((el) => {
          const attr = `data-minitip2-bound-${b.key}`
          if ((el as HTMLElement).getAttribute(attr) === '1') return
          // Indicador visual de não visto
          let host: HTMLElement | null = null
          if (b.indicatorSelector) {
            if (b.indicatorSelector.startsWith('^closest:')) {
              const sel = b.indicatorSelector.replace('^closest:', '')
              host = (el as HTMLElement).closest(sel) as HTMLElement | null
            } else {
              host = (el as HTMLElement).querySelector(b.indicatorSelector) as HTMLElement | null
            }
          }
          if (!host) host = el as HTMLElement
          ensureIndicator(host || el, b.key)
          const onClick = async (e: Event) => {
            if (hasSeen(b.key)) return
            if (!b.defer) {
              // Comportamento antigo (intercepta)
              try { e.preventDefault() } catch { void 0; }
              try { e.stopPropagation() } catch { void 0; }
              const rect = (el as HTMLElement).getBoundingClientRect()
              setActive({ key: b.key, text: b.text, anchorRect: rect, mode: b.mode || 'center' })
              setSeen(b.key, user?.id)
              removeIndicatorsForKey(b.key)
              try { (window as any).Sentry?.addBreadcrumb?.({ category: 'minitip', message: 'open', data: { key: b.key } }) } catch { void 0; }
              return
            }
            // Defer: deixa a ação ocorrer e mostra após abrir
            try { (window as any).Sentry?.addBreadcrumb?.({ category: 'minitip', message: 'defer_click', data: { key: b.key } }) } catch { void 0; }
            window.setTimeout(async () => {
              const anchorEl = await waitForAfterOpen(b)
              const rect = anchorEl?.getBoundingClientRect() || null
              setActive({ key: b.key, text: b.text, anchorRect: rect, mode: b.mode || 'center' })
              setSeen(b.key, user?.id)
              removeIndicatorsForKey(b.key)
            }, 0)
          }
          el.addEventListener('click', onClick, { capture: true })
          handlers.push({ key: b.key, el, onClick })
          try { (el as HTMLElement).setAttribute(attr, '1') } catch { void 0; }
        })
      })
    }

    bindAll()
    const mo = new MutationObserver(() => bindAll())
    mo.observe(document.body, { childList: true, subtree: true })
    moRef.current = mo

    // Eventos públicos
    const onOpen = (e: Event) => {
      const ce = e as CustomEvent<{ key: string }>
      const key = ce.detail?.key
      if (!key) return
      if (hasSeen(key)) return
      const found = bindings.find((b) => b.key === key)
      if (!found) return
      const doOpen = async () => {
        let rect: DOMRect | null = null
        try {
          let el: HTMLElement | null = null
          if (found.selector) el = document.querySelector(found.selector) as HTMLElement | null
          if (!el && found.resolver) {
            const arr = found.resolver(document)
            el = (arr && arr.length > 0 ? (arr[0] as HTMLElement) : null)
          }
          if (!el && found.afterOpenResolver) el = (await waitForAfterOpen(found))
          rect = el?.getBoundingClientRect() || null
        } catch { void 0; }
        setActive({ key, text: found.text, anchorRect: rect, mode: found.mode || 'center' })
        setSeen(key, user?.id)
        removeIndicatorsForKey(key)
      }
      void doOpen()
    }

    const onReset = (e: Event) => {
      const ce = e as CustomEvent<{ key: string }>
      const key = ce.detail?.key
      if (!key) return
      
      // 1. Remover do localStorage
      try { localStorage.removeItem(`minitip:${key}:seen`) } catch { void 0; }
      
      // 2. Habilitar no banco (em background)
      if (user?.id) {
        miniTutorialService.enableMiniTutorial(user.id, key).catch((err) => {
          console.error('[MiniTips2] Error enabling mini tutorial:', err)
        })
      }
      
      // 3. Re-render de indicadores para elementos atuais dessa key
      try {
        const b = bindings.find(x => x.key === key)
        if (b) {
          const selected = b.selector ? Array.from(document.querySelectorAll(b.selector)) : []
          const resolved = b.resolver ? b.resolver(document) : []
          const els = Array.from(new Set([...(selected as Element[]), ...(resolved as Element[])]))
          els.forEach((el) => {
            let host: HTMLElement | null = null
            if (b.indicatorSelector) {
              if (b.indicatorSelector.startsWith('^closest:')) {
                const sel = b.indicatorSelector.replace('^closest:', '')
                host = (el as HTMLElement).closest(sel) as HTMLElement | null
              } else {
                host = (el as HTMLElement).querySelector(b.indicatorSelector) as HTMLElement | null
              }
            }
            if (!host) host = el as HTMLElement
            ensureIndicator(host || el, key)
          })
        }
      } catch { void 0; }
    }

    const onAllReset = () => {
      // Resetar todos os mini tutoriais no localStorage
      bindings.forEach((b) => {
        try {
          localStorage.removeItem(`minitip:${b.key}:seen`)
        } catch { void 0; }
      })
      // Forçar rebind para mostrar os indicadores novamente
      bindAll()
    }

    window.addEventListener('magistral:minitip:open', onOpen as EventListener)
    window.addEventListener('magistral:minitip:reset', onReset as EventListener)
    window.addEventListener('magistral:minitip:all-reset', onAllReset)

    return () => {
      handlers.forEach(({ el, onClick }) => el.removeEventListener('click', onClick, { capture: true } as any))
      if (moRef.current) { moRef.current.disconnect(); moRef.current = null }
      window.removeEventListener('magistral:minitip:open', onOpen as EventListener)
      window.removeEventListener('magistral:minitip:reset', onReset as EventListener)
      window.removeEventListener('magistral:minitip:all-reset', onAllReset)
    }
  }, [bindings, tutorialActive, user?.id])
  // DOTS_VISIBILITY_START
  useEffect(() => {
    // Esconde indicadores (dots) que nÃ£o pertencem ao modal/overlay de app atualmente aberto
    const selectorOverlays = ['.fixed.inset-0', '[role="dialog"]', '[data-modal]'].join(',')
    const isMiniTipsDialog = (el: Element) => (el as HTMLElement).hasAttribute('data-minitip2-dialog')
    const update = () => {
      try {
        const overlays = Array.from(document.querySelectorAll(selectorOverlays)) as HTMLElement[]
        const appOverlays = overlays.filter(el => !isMiniTipsDialog(el))
        const top = appOverlays.length ? appOverlays[appOverlays.length - 1] : null
        const dots = Array.from(document.querySelectorAll('span[data-minitip2-dot-key]')) as HTMLElement[]
        dots.forEach((d) => { if (top) { d.style.setProperty('display', 'none', 'important') } else { d.style.removeProperty('display') } })
      } catch { void 0; }
    }
    update()
    const mo = new MutationObserver(() => update())
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class','style'] })
    window.addEventListener('resize', update)
    return () => { mo.disconnect(); window.removeEventListener('resize', update) }
  }, [])
  // DOTS_VISIBILITY_END

  if (!active) return null

  const close = () => setActive(null)

  if (active.mode === 'tooltip' && active.anchorRect) {
    const tooltipLines = chunkTextByWords(active.text)
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
    const margin = 12
    const centerX = Math.min(Math.max(active.anchorRect.left + active.anchorRect.width / 2, 16), vw - 16)
    const preferTop = active.anchorRect.top > 110
    const containerStyle: React.CSSProperties = preferTop
      ? { ...TOOLTIP_CONTAINER_BASE, left: centerX, top: Math.max(8, active.anchorRect.top - margin), transform: 'translate(-50%, -100%)' }
      : { ...TOOLTIP_CONTAINER_BASE, left: centerX, top: active.anchorRect.bottom + margin, transform: 'translate(-50%, 0)' }

    return (
      <>
        <div style={OVERLAY_STYLE} onClick={close} aria-hidden="true" />
        <div style={containerStyle} role="dialog" data-minitip2-dialog aria-label="Mini tutorial">
          <div style={TOOLTIP_BOX}>
            <div style={BUBBLE_TEXT_WRAPPER}>
              {(tooltipLines.length ? tooltipLines : [active.text]).map((line, idx) => (
                <p key={`tooltip-line-${idx}`} style={BUBBLE_TEXT}>{line}</p>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                style={TOOLTIP_BTN} 
                onClick={close} 
                autoFocus
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.filter = 'brightness(1.1)' }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'brightness(1)' }}
              >
                <IoCheckmark size={16} />
                Entendi
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  const lines = chunkTextByWords(active.text)

  return (
    <>
      <div style={OVERLAY_STYLE} onClick={close} aria-hidden="true" />
      <div style={CENTER_CONTAINER} role="dialog" data-minitip2-dialog aria-label="Mini tutorial">
        <video 
          src="/Imagens/Apis_Video_Basico_webm.webm" 
          autoPlay 
          loop 
          muted 
          playsInline 
          style={IMG_APIS} 
        />
        <div style={BUBBLE_CENTER}>
          <div style={BUBBLE_TAIL} />
          <div style={BUBBLE_TEXT_WRAPPER}>
            {(lines.length ? lines : [active.text]).map((line, idx) => (
              <p key={`bubble-line-${idx}`} style={BUBBLE_TEXT}>{line}</p>
            ))}
          </div>
        </div>
        <button 
          style={BTN} 
          onClick={close} 
          autoFocus
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.filter = 'brightness(1.1)' }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'brightness(1)' }}
        >
          <IoCheckmark size={18} />
          Entendi
        </button>
      </div>
    </>
  )
}

export default MiniTips2












